/**
 * Global Freebie Settings - Automatic Free Gift AJAX Logic
 * 
 * Automatically adds a freebie product when the cart subtotal is >= configured threshold,
 * and removes it when the subtotal falls below the threshold.
 * Prevents loops, handles quantity changes, and refreshes the cart drawer and cart page.
 */
(function() {
  let isRunning = false;

  /**
   * Helper function to identify sections to update
   */
  function getSectionsToRender() {
    const sections = [
      {
        id: 'Cart-Icon-Bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      }
    ];

    const cartItems = document.querySelector('cart-items');
    if (cartItems) {
      const inDrawer = cartItems.hasAttribute('data-in-drawer');
      const sectionId = cartItems.dataset.section;
      if (inDrawer) {
        sections.push({
          id: 'Cart-Drawer',
          section: sectionId,
          selector: '#Cart-Drawer-Details',
        });
      } else {
        sections.push({
          id: 'Main-Cart',
          section: sectionId,
          selector: '#Main-Cart-Details',
        });
      }
    } else {
      // Fallback: search both drawer and main cart elements in DOM
      const drawerDetails = document.getElementById('Cart-Drawer-Details');
      const mainCartDetails = document.getElementById('Main-Cart-Details');
      const cartDrawerEl = document.getElementById('Cart-Drawer');
      const mainCartEl = document.getElementById('Main-Cart');

      if (drawerDetails && cartDrawerEl) {
        sections.push({
          id: 'Cart-Drawer',
          section: cartDrawerEl.dataset.section || 'cart-drawer',
          selector: '#Cart-Drawer-Details',
        });
      }
      if (mainCartDetails && mainCartEl) {
        sections.push({
          id: 'Main-Cart',
          section: mainCartEl.dataset.section || 'main-cart',
          selector: '#Main-Cart-Details',
        });
      }
    }

    return sections;
  }

  /**
   * Updates the theme's cart UI using the section rendering API response
   */
  function updateCartUI(responseJson) {
    const sectionsToRender = getSectionsToRender();
    
    if (window.SectionDynamicUpdate && typeof window.SectionDynamicUpdate.updateSections === 'function') {
      window.SectionDynamicUpdate.updateSections(sectionsToRender, responseJson.sections);
    } else {
      // Fallback section update if helper is not yet initialized
      sectionsToRender.forEach((section) => {
        const elementToReplace =
          document.getElementById(section.id)?.querySelector(section.selector) ||
          document.getElementById(section.id);

        if (elementToReplace && responseJson.sections && responseJson.sections[section.section]) {
          const dom = new DOMParser().parseFromString(responseJson.sections[section.section], 'text/html');
          const element = dom.querySelector(section.selector) || dom;
          if (element) {
            elementToReplace.innerHTML = element.innerHTML;
          }
        }
      });
    }

    // Publish cartUpdate event to notify other sub-components of the theme
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'freebie-script',
        cartData: responseJson,
      });
    }
  }

  /**
   * Validates cart subtotal and adds or removes the freebie product
   */
  async function checkFreebie(cart) {
    if (isRunning) return;

    const settings = window.freebieSettings;
    if (!settings || !settings.enabled || !settings.variantId || !settings.productId) {
      console.log('[Freebie] Freebie feature is disabled or not configured.');
      return;
    }

    // Verify product is available/in stock
    if (settings.available === false) {
      console.log('[Freebie] Selected freebie product is out of stock.');
      return;
    }

    const freebieVariantId = parseInt(settings.variantId);
    const freebieProductId = parseInt(settings.productId);
    const threshold = parseFloat(settings.threshold) * 100; // threshold is in main currency units, convert to cents/paise

    let originalTotalPrice = cart.original_total_price;
    let freebieItem = null;
    let otherItemsCount = 0;

    if (cart.items && Array.isArray(cart.items)) {
      cart.items.forEach(item => {
        if (item.variant_id === freebieVariantId || item.product_id === freebieProductId) {
          freebieItem = item;
        } else {
          otherItemsCount += item.quantity;
        }
      });
    }

    // Calculate cart total excluding the freebie product's price to prevent loops
    let subtotalWithoutFreebie = originalTotalPrice;
    if (freebieItem) {
      subtotalWithoutFreebie -= freebieItem.final_line_price;
    }

    // Target quantity:
    // - Should be 1 if subtotal (excluding freebie) >= threshold AND there is at least one other item in the cart.
    // - Should be 0 otherwise (falls below threshold or cart has no other products).
    let targetQuantity = 0;
    if (subtotalWithoutFreebie >= threshold && otherItemsCount > 0) {
      targetQuantity = 1;
    }

    const currentQuantity = freebieItem ? freebieItem.quantity : 0;

    // If current state matches target state, do nothing
    if (currentQuantity === targetQuantity) {
      return;
    }

    isRunning = true;
    console.log(`[Freebie] Subtotal (excluding freebie): ${subtotalWithoutFreebie / 100}, Threshold: ${threshold / 100}, Current Freebie Qty: ${currentQuantity}, Target Qty: ${targetQuantity}`);

    try {
      const sectionsToRender = getSectionsToRender();
      let responseJson = null;

      if (targetQuantity === 1) {
        // Add or reset freebie to quantity = 1
        if (freebieItem) {
          // If already in cart but quantity is incorrect (e.g. user increased it), update to 1
          console.log('[Freebie] Adjusting freebie quantity in cart to 1.');
          const url = window.routes?.cart_change_url || '/cart/change.js';
          const body = JSON.stringify({
            id: String(freebieVariantId),
            quantity: 1,
            sections: sectionsToRender.map(s => s.section),
            sections_url: window.location.pathname
          });

          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: body
          });
          responseJson = await response.json();
        } else {
          // Freebie not in cart, add it
          console.log('[Freebie] Adding freebie product to cart.');
          const url = window.routes?.cart_add_url || '/cart/add.js';
          const body = JSON.stringify({
            items: [{
              id: freebieVariantId,
              quantity: 1,
              properties: {
                '_is_freebie': 'true'
              }
            }],
            sections: sectionsToRender.map(s => s.section),
            sections_url: window.location.pathname
          });

          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: body
          });
          responseJson = await response.json();
        }
      } else {
        // Remove freebie (either fell below threshold or no other items left in cart)
        console.log('[Freebie] Removing freebie product from cart.');
        const url = window.routes?.cart_change_url || '/cart/change.js';
        const body = JSON.stringify({
          id: String(freebieVariantId),
          quantity: 0,
          sections: sectionsToRender.map(s => s.section),
          sections_url: window.location.pathname
        });

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: body
        });
        responseJson = await response.json();
      }

      if (responseJson) {
        if (responseJson.sections) {
          updateCartUI(responseJson);
        } else {
          // Fallback if sections were not returned directly: fetch cart with sections
          const cartUrl = `${window.routes?.cart_url || '/cart'}?sections=${sectionsToRender.map(s => s.section).join(',')}`;
          const secRes = await fetch(cartUrl);
          if (secRes.ok) {
            const secJson = await secRes.json();
            updateCartUI(secJson);
          }
        }
      }
    } catch (error) {
      console.error('[Freebie] Error updating freebie cart state:', error);
    } finally {
      isRunning = false;
    }
  }

  // Intercept AJAX Fetch calls to update the cart state in real-time
  const originalFetch = window.fetch;
  window.fetch = async function(...args) {
    const response = await originalFetch.apply(this, args);
    const url = typeof args[0] === 'string' ? args[0] : args[0].url;

    if (url && (
      url.includes('/cart/add') || 
      url.includes('/cart/change') || 
      url.includes('/cart/update') || 
      url.includes('/cart/clear')
    )) {
      if (response.ok && !isRunning) {
        try {
          const clonedResponse = response.clone();
          const cartJson = await clonedResponse.json();

          // Wait a short duration to let the DOM update or main requests complete, then synchronize
          setTimeout(async () => {
            let finalCart = cartJson;
            // If the endpoint does not return the full cart items list (like /cart/add.js), fetch it
            if (!cartJson.items || url.includes('/cart/add')) {
              const cartRes = await originalFetch('/cart.js');
              if (cartRes.ok) {
                finalCart = await cartRes.json();
              }
            }
            checkFreebie(finalCart);
          }, 100);
        } catch (err) {
          console.error('[Freebie] Interceptor error:', err);
        }
      }
    }
    return response;
  };

  // Run initial check on page load to sync free gift state
  document.addEventListener('DOMContentLoaded', async () => {
    try {
      const res = await originalFetch('/cart.js');
      if (res.ok) {
        const cart = await res.json();
        checkFreebie(cart);
      }
    } catch (err) {
      console.error('[Freebie] Initial check error:', err);
    }
  });

  // Subscribe to theme's pubsub event as an additional safety net
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Avoid checking if the update was triggered by our own script to prevent recursive calls
      if (event && event.source !== 'freebie-script') {
        let finalCart = event.cartData;
        if (finalCart && finalCart.items) {
          checkFreebie(finalCart);
        } else {
          // Fetch current cart if event does not contain the full cart object
          originalFetch('/cart.js')
            .then(res => res.json())
            .then(cart => checkFreebie(cart))
            .catch(err => console.error('[Freebie] Pubsub sync cart fetch error:', err));
        }
      }
    });
  }
})();
